/**
 * useActiveBulkJobs
 *
 * Polls /my?label=job&key=#job#bulk-action for in-flight bulk action jobs.
 *
 * Backoff:
 *   - ACTIVE_POLL_MS (3 s) while at least one job is in-flight (queued or running)
 *   - IDLE_POLL_MS (10 s) when no jobs are active
 *   - After MAX_BULK_POLL_WALLCLOCK_MS wall-clock ms per job, force-fail the job
 *     and stop polling it to prevent indefinite network traffic for stuck backend
 *     jobs. Wall-clock aligns with the backend 10-minute resolver timeout.
 *
 * On terminal job (status starts with 'JP' = pass, 'JF' = fail, 'JX' = partial):
 *   - Updates the bulkJobs Zustand store via updateJobStatus / markSuppressedIfFast
 *   - Invalidates the TanStack Query cache for the affected entity list
 *   - Shows a failure or partial toast (UX divergence #4 — NO success toast)
 *
 * UX divergence #4:
 *   - Pass → silent (badge decrement + drawer entry are the only signals)
 *   - Fail / Partial → toast.error with "View details" link that opens BulkJobsDrawer
 */

import { useRef } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { invalidateRiskMetrics } from '@/hooks/useRisks';
import { queryClient } from '@/queryclient';
import { useBulkJobsStore } from '@/state/bulkJobs.store';
import { Job, JobStatus } from '@/types';
import { useQuery } from '@/utils/api';

import {
  ACTIVE_POLL_MS,
  IDLE_POLL_MS,
  MAX_BULK_POLL_WALLCLOCK_MS,
} from './constants';
import { parseProgress } from './parseProgress';

/**
 * Derives the BulkJobStatus from the raw server job status string.
 * Server uses prefixed codes like 'JP', 'JF', 'JR', 'JQ', 'JX'.
 */
function serverStatusToBulkStatus(
  rawStatus: string
): 'queued' | 'running' | 'pass' | 'fail' | 'partial' {
  const prefix = rawStatus.slice(0, 2);
  if (prefix === JobStatus.Pass) return 'pass';
  if (prefix === JobStatus.Fail) return 'fail';
  if (prefix === JobStatus.Partial) return 'partial';
  if (prefix === JobStatus.Running) return 'running';
  return 'queued';
}

/**
 * Polls for active bulk action jobs and keeps the Zustand store in sync.
 * Mount once at app root (e.g. TopNavBar or App) so polling starts as soon
 * as the first bulk job is submitted.
 *
 * Uses ACTIVE_POLL_MS / IDLE_POLL_MS from constants.ts (same source of truth
 * as useAsyncExports).  After MAX_BULK_POLL_WALLCLOCK_MS wall-clock milliseconds
 * without a terminal status the job is force-failed to prevent indefinite polling.
 */
export function useActiveBulkJobs() {
  const axios = useAxios({});
  const jobs = useBulkJobsStore(state => state.jobs);
  const updateJobStatus = useBulkJobsStore(state => state.updateJobStatus);
  const markSuppressedIfFast = useBulkJobsStore(
    state => state.markSuppressedIfFast
  );
  const setJobResolvedKeys = useBulkJobsStore(
    state => state.setJobResolvedKeys
  );

  // Per-job wall-clock start time (ms since epoch).  Used to enforce the
  // MAX_BULK_POLL_WALLCLOCK_MS ceiling independent of poll interval switches.
  // Resets automatically when a job transitions out of the in-flight set.
  const pollStartTime = useRef<Map<string, number>>(new Map());

  // Derive in-flight job IDs from the store so we know what to poll for.
  const activeJobIds = Array.from(jobs.values())
    .filter(j => j.status === 'queued' || j.status === 'running')
    .map(j => j.jobId);

  return useQuery({
    queryKey: ['activeBulkJobs', activeJobIds],
    defaultErrorMessage: 'Failed to poll bulk jobs',
    queryFn: async () => {
      if (activeJobIds.length === 0) return [] as Job[];

      // Fetch each in-flight job by key from /my
      const results = await Promise.allSettled(
        activeJobIds.map(jobId =>
          axios
            .get<{ jobs: Job[] }>('/my', {
              params: { label: 'job', key: jobId },
            })
            .then(res => ({ jobId, job: res.data?.jobs?.[0] ?? null }))
        )
      );

      const polledJobs: Array<{ jobId: string; job: Job | null }> = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          polledJobs.push(result.value);
        }
      }

      for (const { jobId, job } of polledJobs) {
        // Record the first poll time for this job (wall-clock start).
        if (!pollStartTime.current.has(jobId)) {
          pollStartTime.current.set(jobId, Date.now());
        }

        // --- Wall-clock ceiling guard ---
        // If the job has not reached a terminal status within
        // MAX_BULK_POLL_WALLCLOCK_MS, force-fail it so the badge decrements
        // and the user is informed.  Wall-clock is used instead of attempt count
        // so that switching between ACTIVE_POLL_MS and IDLE_POLL_MS intervals
        // does not change the effective timeout window.
        const elapsed =
          Date.now() - (pollStartTime.current.get(jobId) ?? Date.now());
        if (elapsed >= MAX_BULK_POLL_WALLCLOCK_MS) {
          const storeJob = useBulkJobsStore.getState().jobs.get(jobId);
          const label = storeJob
            ? `${storeJob.action} ${storeJob.resource}`
            : 'Bulk action';
          updateJobStatus(
            jobId,
            'fail',
            null,
            'Job timed out — no response from server'
          );
          markSuppressedIfFast(jobId);
          toast.error(`${label} timed out`, {
            description:
              'The bulk action did not complete in the expected time. Please try again.',
            action: {
              label: 'View details',
              onClick: () => {
                window.dispatchEvent(new CustomEvent('open-bulk-jobs-drawer'));
              },
            },
            duration: 8000,
          });

          // Invalidate entity list cache so the table refetches fresh data and
          // surfaces any partially-flushed server writes from the timed-out job.
          if (storeJob?.resource) {
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getMy(
                storeJob.resource as Parameters<typeof getQueryKey.getMy>[0]
              ),
            });
          }

          pollStartTime.current.delete(jobId);
          continue;
        }

        if (!job) continue;

        const bulkStatus = serverStatusToBulkStatus(job.status);
        const progress = parseProgress(job.comment);
        updateJobStatus(jobId, bulkStatus, progress, job.comment ?? null);

        // ENG-3057: for query-mode jobs the backend persists resolved keys onto
        // the job record after resolveFromQuery. Once they appear, feed them
        // into the store so per-row dimming can activate for Select-All.
        const currentStoreJob = useBulkJobsStore.getState().jobs.get(jobId);
        if (currentStoreJob && currentStoreJob.pendingKeys.size === 0) {
          const rawKeys = (job.config as Record<string, string> | null)
            ?.resolved_keys;
          if (typeof rawKeys === 'string' && rawKeys.length > 0) {
            try {
              const parsed = JSON.parse(rawKeys);
              if (Array.isArray(parsed) && parsed.length > 0) {
                setJobResolvedKeys(jobId, parsed as string[]);
              }
            } catch {
              // Malformed JSON — skip; backend truncation / future schema changes
              // should not crash the polling loop.
            }
          }
        }

        const isTerminal =
          bulkStatus === 'pass' ||
          bulkStatus === 'fail' ||
          bulkStatus === 'partial';

        if (isTerminal) {
          markSuppressedIfFast(jobId);
          // Clear wall-clock start time on terminal — job will leave activeJobIds.
          pollStartTime.current.delete(jobId);

          // Read fresh store state once for all terminal-block lookups.
          const storeJob = useBulkJobsStore.getState().jobs.get(jobId);

          // Invalidate entity list cache so the table refetches fresh data.
          if (storeJob?.resource) {
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getMy(
                storeJob.resource as Parameters<typeof getQueryKey.getMy>[0]
              ),
            });

            // Risk terminal jobs (e.g. bulk hard-delete) also change dashboard
            // totals — refresh the statistic query family (HealthGrade,
            // open-severity counts) the same way the single-risk mutation path
            // does. Scoped to the risk resource only.
            if (storeJob.resource === 'risk') {
              invalidateRiskMetrics();
            }
          }

          // UX divergence #4: show failure/partial toast — NO success toast.
          if (bulkStatus === 'fail' || bulkStatus === 'partial') {
            const label = storeJob
              ? `${storeJob.action} ${storeJob.resource}`
              : 'Bulk action';

            let toastTitle: string;
            let toastDescription: string;

            if (bulkStatus === 'partial') {
              toastTitle = 'Bulk action partially failed';
              // Provide item counts when progress info is available.
              const desc = progress
                ? `${progress.processed} of ${progress.total} items processed. ${progress.failed} failed.`
                : job.comment || 'Some items could not be processed.';
              toastDescription = desc;
            } else {
              toastTitle = `${label} failed`;
              toastDescription =
                job.comment || 'One or more items could not be processed.';
            }

            toast.error(toastTitle, {
              description: toastDescription,
              action: {
                label: 'View details',
                onClick: () => {
                  // Dispatch a custom event that BulkJobsDrawer can listen to.
                  window.dispatchEvent(
                    new CustomEvent('open-bulk-jobs-drawer')
                  );
                },
              },
              duration: 8000,
            });
          }
        }
      }

      return polledJobs;
    },
    // Backoff: 3s when active jobs exist, 10s when idle.
    // refetchInterval callback receives the query object (TanStack Query v5 API).
    refetchInterval: () => {
      const hasActive = activeJobIds.length > 0;
      return hasActive ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    },
    // Always enabled — when idle the interval is 10s (no user-visible cost).
    enabled: true,
    staleTime: 0,
  });
}
